import React, { useEffect, useRef } from 'react';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_VECTOR_DB,
  CONNECTION_CATEGORY_DATA_MAP,
} from 'js/constants/connectionCategories';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import Database from 'new/components/flows/configure/Database';
import useConfig from 'new/components/flows/types/DirectFlow/hooks/useConfig';
import createFlowService from 'new/components/flows/service/createFlowService';
import { useGetMetaInfoByConnectorType } from 'new/hooks/useGetMetaInfoByConnectorType';
import { useForm } from 'new/elements/Form/useForm';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import useRequests from 'new/state/request/repository/useRequests';
import { useHistory } from 'react-router-dom';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import { NoSQL } from 'new/components/flows/configure/NoSQL/NoSQL';
import { RestAPIDestination } from 'new/components/flows/configure/RestAPIDestination/RestAPIDestination';
import { RestTemplatizedDestination } from 'new/components/flows/configure/RestAPIDestination/RestTemplatizedDestination';
import { getConnectionOrVendorType, isDetectedDataset } from 'js/lib/ResourceUtils';
import { useVendors } from 'new/state/vendors/repository/useVendors';

import { FileUtil } from 'new/utils/FileUtil';
import useFlows from 'new/components/flows/useFlows';
import usePaging from 'new/state/paging/repository/usePaging';
import useFormsAdapter from 'new/state/forms/adapter/useFormsAdapter';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { NavigatorForEditedNexset } from 'new/components/NavigatorForEditedNexset';
import { usePreviousPath } from 'new/hooks/usePreviousPath';
import { normalizeCredentialId } from 'js/lib/utils/credentialUtils';
import { isUiModeCode } from 'new/utils/uiMode';

import useFlowType from '../hooks/useFlowType';
import { DynamicDataMap } from 'new/components/flows/configure/DynamicDataMap/DynamicDataMap';
import { useHeaderMetaConfig } from 'new/components/flows/context/useHeaderMetaConfig';
import useFlowParams from 'new/components/flows/useFlowParams';

const { getSinkName } = createFlowService();

const FORM = FORM_TYPE_CREATE_DIRECT_FLOW;
export const SinkConfigureStep = () => {
  const history = useHistory();
  const { name: metaName, description: metaDescription } = useHeaderMetaConfig();
  const { vendors } = useVendors();
  const { showError } = useRequests();
  const adapter = useFormsAdapter(FORM);
  const { getNexsetSchemaById } = useSchemas();
  const { overrideNextFunction, nextPage } = useForm();
  const { getConnectionCategoryByConnectorType } = usePaging();
  const { sourceConfig, sinkConfig, onSinkConfigChanged } = useConfig();
  const pendingAdvancedSinkConfigRef = useRef<any>(null);
  const flowType = useFlowType();
  const { updateFormData, validate, onBucketSelected } = useFlowsSetup(FORM, flowType);
  const { onSinkMappingUpdated } = useFlows(updateFormData);

  const {
    sink = {},
    source = {},
    nexset = {},
    isNewTable = false,
    isEditMode,
    credentialId,
    connectionType,
    selectedBucket,
    onTableSelected,
    selectedCredential,
    selectedConnection,
    table: selectedTable,
    toggleDestinationDraft,
    destinationDraftValue,
    showEditedNexsetView,
  } = useDirectFlow();

  const previousPath = usePreviousPath();
  const { isModal, onDismiss } = useFlowParams();
  const flowService = createFlowService();

  const isNexsetDetected = isDetectedDataset(nexset);

  /**
   * Get a sink connection or vendor type
   */
  const connection = sink?.sink_type
    ? getConnectionOrVendorType(sink, vendors, 'sink_type', 'vendor_id')
    : null;

  const connectionCategory = connection
    ? getConnectionCategoryByConnectorType(connection)
    : getConnectionCategoryByConnectorType(selectedConnection);

  const meta = useGetMetaInfoByConnectorType(selectedConnection);

  const createSink = async (sinkInfo: any) => {
    const { error, data } = await tryCatchAsync(
      adapter.createSink({
        sinkInfo,
        credentialId,
        formType: FORM,
        sinkType: sinkConfig?.advanced_templatized_api ? sinkConfig?.sink_type : selectedConnection,
      }),
    );

    if (error) return { error: true, message: error.message };
    return { data: data?.payload?.formData?.sink };
  };

  const updateSink = async (id: number, sinkInfo: any) => {
    const newSinkInfo = { id, ...sinkInfo };
    const { error, data } = await tryCatchAsync(adapter.updateSink({ sinkInfo: newSinkInfo }));

    if (error) return { error: true, message: error.message };
    return { data: data?.payload?.formData?.sink };
  };

  const getName = (config = sinkConfig) => {
    let { name, sinkName } = config || {};
    return (
      name ||
      sinkName ||
      sink?.name ||
      getSinkName({
        selectedBucket,
        selectedTable,
        dataMapSettings: { name: '' },
        connectionCategory,
        selectedCredential,
        metaInfo: meta,
        selectedConnection,
      })
    );
  };

  const buildSinkInfo = (config = sinkConfig) => {
    let { name, description, sourceName, sourceDescription, ...sink_config } = config || {};
    const { sink_type = selectedConnection } = sink_config;

    const sinkInfo: any = {
      flow_type: flowType,
      description: metaDescription || description || sourceDescription || '',
      sink_type,
      sink_config,
      name: metaName || getName(config),
    };

    if (isNewTable) {
      sinkInfo.create_destination = 'true';
    }

    if (!sink?.id) {
      sinkInfo.data_set_id = nexset?.id;
    }

    return sinkInfo;
  };

  const credentialIdToSend = normalizeCredentialId(credentialId);

  const handleSinkCreationOrUpdate = async (config = sinkConfig): Promise<{ id?: number }> => {
    const info = buildSinkInfo(config);
    if (sink?.id && credentialIdToSend != null) {
      info.data_credentials_id = credentialIdToSend;
    }
    // Handle data_map specific configuration
    if (connectionCategory === CONNECTION_CATEGORY_DATA_MAP) {
      const { name, description, data_map } = sinkConfig;

      const finalSinkInfo = {
        ...info,
        name,
        description,
        sink_type: selectedConnection,
        data_set_id: nexset?.id,
        data_map: {
          map_primary_key: data_map?.map_primary_key,
          data_defaults: data_map?.data_defaults || {},
          emit_data_default: true,
          public: false,
        },
      };
      if (sink?.id && credentialIdToSend != null) {
        finalSinkInfo.data_credentials_id = credentialIdToSend;
      }

      const { error, data, message } = sink?.id
        ? await updateSink(sink.id, finalSinkInfo)
        : await createSink(finalSinkInfo);

      if (error) {
        showError(message);
        return null;
      }

      return data;
    }

    const { error, data, message } = sink?.id
      ? await updateSink(sink?.id, info)
      : await createSink(info);

    if (error) {
      console.error('Error while creating/updating sink info:', message);
      return null;
    }
    return data;
  };

  const handleSinkNavigation = (history: any, newSink: any) => {
    if (!!newSink.id) {
      if (isModal) {
        onDismiss?.({ addedDataSinkId: newSink.id, ...newSink });
        return;
      }
      if (sink?.id) {
        history.goBack();
      } else {
        history.replace(flowService.getPathToGoAfterCreation(previousPath));
      }
    }
  };

  const validateDataMap = () => {
    const { name, data_map } = sinkConfig;

    if (!name) {
      showError('Please enter a name');
      return false;
    }

    if (!data_map?.map_primary_key) {
      showError('Please select a primary key');
      return false;
    }

    return true;
  };

  const onClickNextStep = async () => {
    const effectiveSinkConfig = pendingAdvancedSinkConfigRef.current || sinkConfig;

    if (!isUiModeCode(effectiveSinkConfig)) {
      // Use specific validation for data_map
      if (connectionCategory === CONNECTION_CATEGORY_DATA_MAP) {
        const isValid = validateDataMap();
        if (!isValid) return false;
      } else {
        // Use default validation for other types
        const invalid = !validate(showError);

        if (invalid) return false;
      }
    }

    const createdOrUpdatedSink = await handleSinkCreationOrUpdate(effectiveSinkConfig);
    pendingAdvancedSinkConfigRef.current = null;

    if (createdOrUpdatedSink) {
      if (destinationDraftValue) {
        updateFormData({ showEditedNexsetView: true });
      } else {
        handleSinkNavigation(history, createdOrUpdatedSink);
      }
      return Boolean(createdOrUpdatedSink.id);
    }
  };

  const onSaveModeAdvanced = (newSinkConfig: any) => {
    pendingAdvancedSinkConfigRef.current = newSinkConfig;
    onSinkConfigChanged(newSinkConfig, null, null);
  };

  const redirectForEditedNexset = (path: string) => {
    if (isModal && !path.includes('edit')) {
      onDismiss?.({ addedDataSinkId: sink.id, ...sink });
      return;
    }

    history.replace(path);
  };

  useEffect(() => {
    overrideNextFunction(async () => {
      return await onClickNextStep();
    });
  }, [onClickNextStep]);

  useEffect(() => {
    return () => {
      overrideNextFunction(null);
    };
  }, []);

  if (!source || !nexset || !selectedConnection) {
    return <CenteredLoader />;
  }

  /**
   * If the new resource is created|updated and there is changes on the payload, show the EditNexset view.
   */
  if (showEditedNexsetView) {
    return (
      <NavigatorForEditedNexset
        dataSetId={nexset?.id}
        isNexsetDetected={isNexsetDetected}
        redirectAfterCompletion={redirectForEditedNexset}
      />
    );
  }

  if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
    return (
      <Database
        connectionType="SINK"
        sink={sink}
        source={source}
        nexset={nexset}
        formType={FORM}
        isNewTable={isNewTable}
        flowType={flowType}
        sinkConfig={sinkConfig}
        credentialId={credentialId}
        sourceConfig={sourceConfig}
        selectedBucket={selectedBucket}
        selectedConnection={selectedConnection}
        isEditMode={isEditMode}
        selectedTable={selectedTable}
        onTableSelected={onTableSelected}
        onSinkConfigChanged={onSinkConfigChanged}
        onSinkMappingUpdated={(newSinkMapping, auto) => {
          onSinkMappingUpdated({
            sinkConfig,
            isEditMode,
            connectionCategory: CONNECTION_CATEGORY_DATABASE,
          })(newSinkMapping, auto);
        }}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
      />
    );
  }

  const nexsetSchema = getNexsetSchemaById(nexset?.id);

  if (connectionCategory === CONNECTION_CATEGORY_NOSQL) {
    return (
      <NoSQL
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        sink={sink}
        nexset={nexset}
        flowType={flowType}
        sinkConfig={sinkConfig}
        nexsetSchema={nexsetSchema}
        sourceConfig={sourceConfig}
        selectedBucket={selectedBucket}
        selectedCredential={selectedCredential}
        selectedConnection={selectedConnection}
        onSinkConfigChanged={onSinkConfigChanged}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
      />
    );
  }

  if (
    connectionCategory === CONNECTION_CATEGORY_FILE ||
    connectionCategory === CONNECTION_CATEGORY_VECTOR_DB
  ) {
    const allowCustomPrefixEditor =
      connectionCategory === CONNECTION_CATEGORY_FILE &&
      FileUtil.isAllowedToUseCustomPrefixEditor(selectedBucket, sinkConfig);

    return (
      <FileSystem
        formType={FORM}
        flowType={flowType}
        sink={sink}
        source={source}
        nexset={nexset}
        sinkConfig={sinkConfig}
        credentialId={credentialId}
        sourceConfig={sourceConfig}
        connectionType={connectionType}
        selectedBucket={selectedBucket}
        selectedConnection={selectedConnection}
        nexsetSchema={nexsetSchema}
        allowCustomPrefixEditor={allowCustomPrefixEditor}
        onBucketSelected={onBucketSelected}
        onSinkConfigChanged={onSinkConfigChanged}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
      />
    );
  }

  if (connectionCategory === CONNECTION_CATEGORY_REST) {
    return (
      <RestAPIDestination
        formType={FORM}
        flowType={flowType}
        sink={sink}
        nexset={nexset}
        isEditMode={Boolean(sink?.id)}
        sinkConfig={sinkConfig}
        selectedConnection={selectedConnection}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
        toggleDestinationDraft={toggleDestinationDraft}
      />
    );
  }

  if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API) {
    return (
      <RestTemplatizedDestination
        formType={FORM}
        flowType={flowType}
        sink={sink}
        nexset={nexset}
        isEditMode={Boolean(sink?.id)}
        sinkConfig={sinkConfig}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
        toggleDestinationDraft={toggleDestinationDraft}
      />
    );
  }

  if (connectionCategory === CONNECTION_CATEGORY_DATA_MAP) {
    return (
      <DynamicDataMap
        formType={FORM}
        flowType={flowType}
        sink={sink}
        nexset={nexset}
        nexsetSchema={nexsetSchema}
        sinkConfig={sinkConfig}
        selectedConnection={selectedConnection}
        isEditMode={isEditMode}
        onSinkConfigChanged={onSinkConfigChanged}
        onSaveAdvancedMode={onSaveModeAdvanced}
        nextFormStage={nextPage}
      />
    );
  }
  return null;
};
